import { Component, DestroyRef, EventEmitter, inject, Input, Output, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { HeadingComponent } from '../../../../../shared/components/heading/heading.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { PasswordModule } from 'primeng/password';
import { DividerModule } from 'primeng/divider';
import { DropDown } from '../../../../client/dashboard/settings/settings.model';
import {
  Reviewer,
  SearchMarket,
} from '../../../../../store/reviewer/reviewer.interface';
import { SearchMarketService } from '../../../../../shared/services/search-market.service';
import { ReviewerService } from '../../../../../shared/services/reviewer.service';
import { JobTypeService } from '../../../../../shared/services/job-type.service';
import { lastValueFrom, Observable, Subject, from, switchMap, exhaustMap, catchError, EMPTY } from 'rxjs';
import { ToastService } from '../../../../../shared/services/toast.service';
import { UserService } from '../../../../../shared/services/user.service';
import { ResetPasswordData, LeaveFirm } from '../../../../../store/user/user.interface';
import { AuthService } from '../../../../../shared/services/auth.service';
import { ProfileInformationClass } from '../../../../../shared/components/profile-information/profile-information.component';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationDialogService } from '../../../../../shared/services/confirmation-dialog.service';
import { MergeAccountComponent } from '../merge-account-dialog/merge-account-dialog.component';
import { EmittersService } from '../../../../../shared/services/emitters.service';
import { UserRoles, constants } from '../../../../../shared/constants/constants';
import { Router } from '@angular/router';
import { ConfigurationService } from '../../../../../shared/services/configuration.service';
import { HelperService } from '../../../../../shared/services/helper.service';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';
import { environment } from '../../../../../../environments/environment';
import { StorageService } from '../../../../../shared/services/storage.service';
@Component({
  selector: 'app-contact-information',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    FloatLabelModule,
    CalendarModule,
    InputTextModule,
    DropdownModule,
    HeadingComponent,
    MultiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    PasswordModule,
    DividerModule,
    TooltipModule,
    PipesModule,
    CheckboxModule
  ],
  templateUrl: './contact-information.component.html',
  styleUrl: './contact-information.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DynamicDialogRef]
})
export class ContactInformationComponent extends ProfileInformationClass{
  @Output() submitData: EventEmitter<Reviewer> = new EventEmitter();
  @Input() from?: string = '';
  @Input() firmId?: string = '';
  @Input() mergeInvite: boolean = false;
  @Input() currentStep: string = '';
  searchMarketDropdown: DropDown[] = [];
  jobType: DropDown[] = [];
  verificationCode!: FormControl;
  searchMarketValues: SearchMarket[] = [];
  jobCategoryValues: string[] = [];
  showPasswordField: boolean = false;
  ref: DynamicDialogRef | undefined;
  skillsCriteriaListing: any;
  existingCriterias: any;
  isJobTypeDisabled = false;
  isPwcEnabled: boolean = false;
  leaveRequestInProgress: boolean = false;
  breadcrumbTitleOptions = [
    { label: 'Profile' }, 
    { label: 'General information' }
  ];
  title = environment.title;
  privacyPolicyUrl = environment.privacyPolicyUrl;
  termsOfServiceUrl = environment.termsOfServiceUrl;
  loadingPrivacyPolicyStatus: boolean = false;
  // Add properties to track state
  isPrivacyPolicyAccepted: boolean = false;
  showPrivacyPolicyField: boolean = false;
  privacyPolicyUpdating: boolean = false;
  isSkipButtonEnabled: boolean = false;
  private submitSubject = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  constructor(
    private searchMarketService: SearchMarketService,
    private confirmationDialogService: ConfirmationDialogService,
    private jobTypeService: JobTypeService,
    private authService: AuthService,
    private eventEmitterService: EmittersService,
    private configurationService: ConfigurationService,
    private confirmationService: ConfirmationDialogService,
    private toast: ToastService,
    fb: FormBuilder,
    reviewerService: ReviewerService,
    toastService: ToastService,
    userService: UserService,
    router: Router,
    reviewerDetailRef: DynamicDialogRef,
    dialogService: DialogService,
    helperService: HelperService,
  ) {
    super(fb, reviewerService, toastService, userService, router, reviewerDetailRef, dialogService, helperService)
  }

  async ngOnInit(): Promise<void> {
    this.loadingPrivacyPolicyStatus = true;
    this.isPwcEnabled = this.configurationService.getPwcFlag();
    this.getTenant();
    this.initializeForm();
    await this.getReviewerInfo();
    this.isSkipButtonVisible();
    this.updateValidators();
    await this.setSearchMarketDropdown();
    await this.setJobType();
    this.isPhoneVerified = this.reviewerInfo?.profile?.contact?.isPhoneVerified;
    this.populateData();
    this.isJobTypeDisabled = this.currentTenant.jobCategoryAddedBy && this.currentTenant.jobCategoryAddedBy === UserRoles.firmManager;

    // Check if privacy policy is already accepted
    this.isPrivacyPolicyAccepted = this.reviewerInfo?.privacyPolicy?.isAccepted || false;
    
    // Hide privacy policy field if already accepted (in edit mode)
    this.showPrivacyPolicyField = !this.isPrivacyPolicyAccepted;
    
    if (this.isPrivacyPolicyAccepted) {
      // Set value to true but don't show the field
      this.profileForm.get('profile')?.get('privacyPolicy')?.setValue(true);
    }
    
    this.loadingPrivacyPolicyStatus = false;

    this.submitSubject.pipe(
      exhaustMap(() => this.executeSubmit().pipe(
        catchError((error) => {
          return EMPTY;
        })
      )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }


  showLeaveFirmConfirmation() {
    this.leaveRequestInProgress = true;
    this.confirmationService.confirm({
      header: `Permanently leave ${this.currentTenant.firm.name}`,
      icon: 'pi pi-exclamation-triangle',
      rejectLabel:'Cancel',
      acceptLabel:'Leave',
      message: `You are about to leave ${this.currentTenant.firm.name}. This is a permanent request that cannot be undone without ${this.title} support staff assistance. Continue?`,
    
      accept:async () => {
           const payload: LeaveFirm = {
              reason : constants.userDeleteReasonsText.USER_REQUESTED,
              comments: "Candiate Initiated Archival",
              firm : this.currentTenant.firm._id,
              candidateInitiated: true
            }
            try{
              await lastValueFrom(this.userService.leaveFirmPermenant(this.reviewerInfo._id, payload));
              this.toast.showSuccess('User archived successfully');
              this.currentTenant.isDeleted = true;
              this.leaveRequestInProgress = false;
              this.authService.logout();
            } catch(e:any){
               this.leaveRequestInProgress = false;
            }
      },
    reject: () => {
      this.leaveRequestInProgress = false;
    }
  }

  
  );
  }

  override initializeForm(): void {
    super.initializeForm();

    const profileGroup = this.profileForm.get('profile') as FormGroup;
    profileGroup.addControl('jobCategories', this.fb.control('', !this.currentTenant?.firm.isSingleJobTypeEnabled ? Validators.required : null));
    profileGroup.addControl('searchMarkets', this.fb.control('', !this.currentTenant?.firm.isSingleSearchMarketEnabled ? Validators.required : null));
    this.profileForm.addControl('password', this.fb.control('', Validators.required));

    // Add privacy policy checkbox
    profileGroup.addControl('privacyPolicy', this.fb.control(false, Validators.required));
  }

  override async getReviewerInfo(): Promise<void> {
    await super.getReviewerInfo();
    this.searchMarketValues = this.reviewerInfo?.profile?.searchMarkets.map(
      (item: any) => item._id
    );
    this.jobCategoryValues = this.reviewerInfo?.profile?.jobCategories.map(
      (item: any) => item
    );
      this.showPasswordField = this.reviewerInfo && this.reviewerInfo.externalInvite && this.reviewerInfo.lastPasswordChangedAt === null ? true : false;
    
   
    if(this.showPasswordField){
      let verifiedTenantIndex = this.reviewerInfo.tenants.findIndex((e: any) => e.reviewerStatus === constants.userVerificationStatus.verified || e.reviewerStatus === constants.userVerificationStatus.pendingAdminVerification);
      this.showPasswordField = verifiedTenantIndex < 0;
    }
    if (!this.showPasswordField) {
      this.disbalePasswordField();
    }
  }

  async setSearchMarketDropdown(): Promise<void> {
    const firm = this.firmId ? this.firmId : this.userFirmId;
    const res = await lastValueFrom(
      this.searchMarketService.getFirmSearchMarkets(firm.toString())
    );
    res.forEach((item: any) => {
      if(item.isActive){
        this.searchMarketDropdown.push({
          label: item?.name,
          value: item?._id,
        });  
      }
    });
  }

  async setJobType(): Promise<void> {
    const firm = this.firmId ? this.firmId : this.userFirmId;
    const res = await lastValueFrom(
      this.jobTypeService.getFirmJobType(firm.toString())
    );
    res.forEach((item: any) => {
      if(!item.isArchived ||!item.isDeleted){
        this.jobType.push({
          label: item?.name,
          value: item?._id,
        });  
      }
    });
  }

  override async sendVerificationCode(): Promise<void> {
    this.authService
      .sendAdminVerificationPhoneSms(
        this.reviewerInfo?._id,
        this.profileForm.get('profile.contact.home')?.value.replace(/-/g, '')
      )
      .subscribe({
        next: async (res) => {
          if (res.message === 'Phone number successfully verified') {
            this.isPhoneVerified = true;
            this.disablePhoneField();
          } else {
            this.toastService.showSuccess(res.message);
          }
        },
      });
  }

  disbalePasswordField(): void {
    (this.profileForm.get('password') as FormControl).disable();
  }
  override populateData(): void {
    super.populateData();
    const validSearchMarketValues = this.searchMarketDropdown.map(s => s.value);
    this.setFormValues('profile.searchMarkets', this.reviewerInfo?.profile?.searchMarkets.map(market => market._id || market).filter(id => validSearchMarketValues.includes(id)));
    const validJobTypeValues = this.jobType.map(j => j.value);
    this.setFormValues('profile.jobCategories', this.reviewerInfo?.profile?.jobCategories.map(job => job._id || job).filter(id => validJobTypeValues.includes(id)));
  }

  extractDropdownValues(values: any): any {
    const extractedArray: string[] = values.map(
      (category: any) => category.value
    );
    return extractedArray;
  }

  onSearchMarketDropdownChange($event: any): void {
    const selectedValues = $event.value;
    if($event.itemValue){
      const removedItems = !selectedValues.includes($event?.itemValue) ? $event.itemValue : {};
      this.searchMarketValues = this.searchMarketValues.filter(value => value !== removedItems.value || '');
    }
    this.searchMarketValues = [
      ...new Set([...this.searchMarketValues, ...selectedValues.map((item: any) => item.value)]),
    ];
  }

  onJobCategoryDropdownChange($event: any): void {
    const selectedValues = $event.value;
    if($event.itemValue){
      const removedItems = !selectedValues.includes($event?.itemValue) ? $event.itemValue : {};
      this.jobCategoryValues = this.jobCategoryValues.filter(value => value !== removedItems.value || '');
    }
    this.jobCategoryValues = [
      ...new Set([...this.jobCategoryValues, ...selectedValues.map((item: any) => item.value)]),
    ];
  }


  override onSubmit(): void {
    this.profileForm.markAllAsTouched();

    if(this.currentTenant.firm.isSingleSearchMarketEnabled && (!this.profileForm.get('profile.searchMarkets')?.value || this.profileForm.get('profile.searchMarkets')?.value.length === 0)) {
      this.profileForm.get('profile')?.get('searchMarkets')?.setValue([this.currentTenant.firm.selectedSingleSearchMarketId]);
    }

    const privacyPolicyControl = this.profileForm.get('profile')?.get('privacyPolicy');
    const privacyPolicyAccepted = privacyPolicyControl?.value === true;

    if (!privacyPolicyAccepted && this.from === 'onboarding') {
      this.toastService.showError('You must accept the Privacy Policy to continue');
      return;
    }

    if (!this.isPhoneVerified) {
      this.toastService.showError('Phone number not verified');
      return;
    }

    if (this.profileForm.valid) {
      this.isPrivacyPolicyAccepted = true;
      this.submitSubject.next();
    }
  }

  private executeSubmit(): Observable<any> {
    this.privacyPolicyUpdating = false;
    if (this.showPasswordField) {
      const currentPassword = this.profileForm.value.password;
      const passwordData: ResetPasswordData = {
        externalInvite: this.reviewerInfo.externalInvite,
        recoverHash: this.reviewerInfo.recoverHash,
        newPassword: currentPassword,
        confirmPassword: currentPassword
      };
      return this.userService.resetPassword(passwordData).pipe(
        switchMap((resp: any) => {
          this.reviewerInfo.lastPasswordChangedAt = resp?.lastPasswordChangedAt;
          this.reviewerInfo.isPasswordUpdated = resp?.isPasswordUpdated;
          return this.executeUpdateReviewer();
        })
      );
    } else {
      return this.executeUpdateReviewer();
    }
  }
  
  override updateReviewer() {
    this.executeUpdateReviewer().subscribe();
  }

  private executeUpdateReviewer(): Observable<any> {
    const payload: Reviewer = {
      ...this.reviewerInfo,
      privacyPolicy: {
        isAccepted: true,
        updatedAt: new Date()
      },
      profile: {
        ...this.reviewerInfo.profile,
        contact: {
          ...this.profileForm.get('profile.contact')?.value,
          state: this.profileForm.get('profile.contact.state')?.value?.name || '',
          home: this.profileForm.get('profile.contact.home')?.value?.replace(/-/g, ''),
          isPhoneVerified: this.isPhoneVerified,
        },
        jobCategories: this.profileForm.get('profile.jobCategories')?.value,
        searchMarkets: this.profileForm.get('profile.searchMarkets')?.value,
        ...(this.currentTenant?.firm.isCitizenshipStatusRequired && { immigrationStatus : this.profileForm.get('profile.immigrationStatus')?.value?.value })
      },
      firstName: this.profileForm.get('firstName')?.value,
      lastName: this.profileForm.get('lastName')?.value,
      preferredFirstName: this.profileForm.get('preferredFirstName')?.value || '',
      email: this.profileForm.get('email')?.value,
      ...(this.currentStep && { currentStep: this.currentStep }),
    };

    return this.reviewerService.updateReviewer(payload).pipe(
      switchMap((res) => from((async () => {
        this.reviewerInfo = res;
        this.toastService.showSuccess('Updated successfully');
        this.reviewerService.fetchReviewerAndUpdate();
        await this.save();
        await super.getReviewerInfo();
        this.submitData.emit(res);
        this.eventEmitterService.skillAndExperienceEmitter.emit(res);
        this.eventEmitterService.credentialEmitter.emit(res);
        if (this.from !== 'onboarding') {
          this.onBack();
        }
      })()))
    );
  }

  mergeAccount(){
    this.ref = this.dialogService.open(MergeAccountComponent, {
      header: `Merge ${this.title} Account`,
      width: '30vw',
      contentStyle: { overflow: 'hidden' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
    });
    this.ref.onClose.subscribe((data: any) => {
      if (data) {
        const firmId = this.firmId ? this.firmId : this.userFirmId;
        this.reviewerService.mergeAccount(firmId, data.email).subscribe((reviewer) => {
          this.toastService.showSuccess(`Account merge email has been sent to ${data.email}. Please follow the link to merge this invite into your account.`);
        });
      }
    });
  }

  formatContact(event: any): void {
    let input = event.target.value.replace(/\D/g, '');
    let formattedInput = '';
    if (input.length > 0) {
      formattedInput = input.substring(0, 3);
  }
  if (input.length > 3) {
      formattedInput += '-' + input.substring(3, 6);
  }
  if (input.length > 6) {
      formattedInput += '-' + input.substring(6, 10);
  }
    event.target.value = formattedInput;  
  }

  async save(){
    this.reviewerService.getFilteredCriteriaBasedUserSkills(this.reviewerInfo._id).subscribe((skillsAndExperience) => {
      this.skillsCriteriaListing = skillsAndExperience
      let criterias:any = {};
      this.skillsCriteriaListing.map((criteriaItem:any)=>{
        criterias = this.getCriterias(criteriaItem);
      });
      const payload = {
        criterias: {
          ...criterias,
        },
        reviewerId: this.reviewerInfo._id,
        // MP-5736: '' for a reviewer's own save, so this is a no-op here today.
        actingFirmId: this.userService.getActingChildFirmId()
      }
      this.reviewerService.saveCriteriaReviewer(payload).subscribe((d:any)=>{
      })
    })
  }

  getCriterias(criteriaItem: any){
    let criterias: any = {};
    let tenantCriteriaFound = false;
    this.reviewerInfo.tenants.forEach((tenant: any) => {
      tenant.criterias.forEach((tenantCriteria: any) => {
        if (tenantCriteria.criteriaId === criteriaItem._id) {
          criterias[criteriaItem.name] = {
            selectedOptions: tenantCriteria.criteriaSelectedOption,
            jobCategory: tenantCriteria.jobCategory,
            criteriaId: tenantCriteria.criteriaId,
          };
          tenantCriteriaFound = true;
        }
      });
      if (!tenantCriteriaFound) {
        criterias[criteriaItem.name] = {
          selectedOptions: [],
          jobCategory: criteriaItem.jobCategory.map((category: any) => category._id),
          criteriaId: criteriaItem._id
        };
      }
    });
    return criterias;
  }

  onBack(): void {
    this.navigateToMainProfile();
  }

  isSkipButtonVisible() {
    this.isSkipButtonEnabled = this.helperService.isSkipButtonVisible(this.reviewerInfo, this.from || '');
  }

  navigateToDashboard(): void {
    this.helperService.navigateToDashboard(this.reviewerInfo);
  }
}
